import type { DateOperatorFilter, LineItemEndDateFilter, RangeFilter } from '../../../../apis/graphql';
import type { DropdownOption } from '../../../../common/Dropdown';
import type { SearchParamsValuesType } from '../../../../constants';
import type { Nullable } from '../../../../models';

type OptionType = { value: string; label: string };

export enum ComparisonFilterSubType {
  LINE_ITEM_END_DATE = 'LINE_ITEM_END_DATE',
  COMPLETIONS_REMAINING = 'COMPLETIONS_REMAINING',
}

export type DropdownFilterValue = (string | DropdownOption)[];

export type FilterValue = DropdownFilterValue | RangeFilter | LineItemEndDateFilter | DateOperatorFilter | boolean;

export type SelectedFiltersValues = Partial<Record<SearchParamsValuesType, Nullable<FilterValue>>>;

export type BooleanFilterOptions = {
  yes: string;
  no: string;
};

export type MultiDropdownPlaceholder = {
  dropdownButtonPlaceholder: string;
  searchInputPlaceholder: string;
};

export enum AdditionalFilterType {
  CHECKBOX = 'CHECKBOX',
  DROPDOWN = 'DROPDOWN',
  RADIOBUTTON = 'RADIOBUTTON',
  BOOLEAN = 'BOOLEAN',
  Chip = 'Chip',
  RANGE = 'RANGE',
  COMPARISON = 'COMPARISON',
  CALENDAR = 'CALENDAR',
}

export type IncludeExcludeOptions = {
  include: SearchParamsValuesType;
  exclude: SearchParamsValuesType;
};

export type AdditionalFilterDefault = {
  searchParam: SearchParamsValuesType;
  includeExcludeOptions?: IncludeExcludeOptions;
  label: string;
  // Identifies where the definition came from. GQL-config-driven definitions are tagged
  // 'config'; hardcoded definitions leave this undefined. Used to key dropdown chips so a
  // chip remounts when its useOptions implementation shape changes (hardcoded <-> config),
  // preventing a Rules-of-Hooks violation when the same fiber would otherwise be reused.
  optionsSource?: 'config';
};

export type UseOptions = {
  options: OptionType[];
  loading?: boolean;
  error?: string;
  hasMore?: boolean;
  searching?: boolean;
  searchTerm?: string;
  onNext?: () => Promise<void>;
  onResetOptions?: () => void;
  onSearch?: (searchTerm: string) => void;
};

export type ConvertValueUtil = (value: number | null) => number | null;

export type RangeOptions = {
  minPossibleValue?: number;
  maxPossibleValue?: number;
  isPercentageValue?: boolean;
  convertToDisplayValue?: ConvertValueUtil;
  convertToSaveValue?: ConvertValueUtil;
  valuePrefix?: string | null;
  valueSuffix?: string | null;
  displayMultiplier?: number | null;
  valueDecimals?: number | null;
};

export type AdditionalFilterRange = AdditionalFilterDefault & {
  value: RangeFilter | null;
  type: AdditionalFilterType.RANGE;
  validationFn?: (value: RangeFilter) => string | null;
  selectedFilterValues?: RangeFilter | null;
  helperText?: string;
  rangeOptions?: RangeOptions;
};

export type AdditionalLineItemEndDateFilter = AdditionalFilterDefault & {
  type: AdditionalFilterType.COMPARISON;
  subType: ComparisonFilterSubType.LINE_ITEM_END_DATE;
  value: LineItemEndDateFilter | null;
  selectedFilterValues?: LineItemEndDateFilter | DateOperatorFilter | null;
};

export type AdditionalCompletionsRemainingFilter = AdditionalFilterDefault & {
  type: AdditionalFilterType.COMPARISON;
  subType: ComparisonFilterSubType.COMPLETIONS_REMAINING;
  value: RangeFilter | null;
  selectedFilterValues?: RangeFilter | null;
};

export type AdditionalFilterDropdown = {
  type: AdditionalFilterType.CHECKBOX | AdditionalFilterType.DROPDOWN | AdditionalFilterType.RADIOBUTTON;
  useOptions: () => UseOptions;
  value: string[] | null;
  multiOptionalLabel: string;
  isSearchable?: boolean;
  selectedFilterValues?: DropdownFilterValue | null;
  multiDropdownPlaceholder?: MultiDropdownPlaceholder;
} & AdditionalFilterDefault;

export type AdditionalFilterBoolean = {
  type: AdditionalFilterType.BOOLEAN;
  options: BooleanFilterOptions;
  value: boolean | null;
  selectedFilterValues?: boolean | null;
} & AdditionalFilterDefault;

export type AdditionalFilterCalendar = AdditionalFilterDefault & {
  type: AdditionalFilterType.CALENDAR;
  value: DateOperatorFilter | null;
  selectedFilterValues?: DateOperatorFilter | null;
};

export type AdditionalFilterChip = {
  type: AdditionalFilterType.Chip;
  value: string;
} & AdditionalFilterDefault;

export type AdditionalFilterInDropdown =
  | AdditionalFilterDropdown
  | AdditionalFilterBoolean
  | AdditionalFilterRange
  | AdditionalFilterCalendar
  | AdditionalLineItemEndDateFilter
  | AdditionalCompletionsRemainingFilter;

export type AdditionalFilter = AdditionalFilterChip | AdditionalFilterInDropdown;

export type AdditionalFilterValue = string[] | RangeFilter | LineItemEndDateFilter | boolean;

export const DEFAULT_BOOLEAN_OPTIONS: BooleanFilterOptions = {
  yes: 'Yes',
  no: 'No',
};

export const DO_NOT_TRAFFIC_BOOLEAN_OPTIONS: BooleanFilterOptions = {
  yes: 'Line is not eligible for trafficking',
  no: 'Line is eligible for trafficking',
};

export const DEFAULT_BRAND_DROPDOWN_BUTTON_PLACEHOLDER = 'Select brand(s)';
export const DEFAULT_INDUSTRY_DROPDOWN_BUTTON_PLACEHOLDER = 'Select industry(s)';
export const DEFAULT_AGENCY_DROPDOWN_BUTTON_PLACEHOLDER = 'Select agency(s)';

export const DEFAULT_BRAND_SEARCH_INPUT_PLACEHOLDER = 'Search by brand name';
export const DEFAULT_INDUSTRY_SEARCH_INPUT_PLACEHOLDER = 'Search by industry name';
export const DEFAULT_AGENCY_SEARCH_INPUT_PLACEHOLDER = 'Search by agency name';

export const FILTER_LABELS_WITH_FIXED_ORDER = ['Ad Status', 'Line Item Status', 'Pacing Status'];

export const PERCENT_MULTIPLIER = 100;
